import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    fetchDownloadDicomSeries,
    fetchRequestImageDownload,
} from '~/backend/connector-api/connector-components';
import { withRetryOn202 } from '~/backend/connector-api/withRetryOn202';
import { Image3D } from '~/domain/patient/image3d';
import { imageListVersionState } from '~/state/atoms/image/imageAtoms';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import { AvailableImage } from './usePrimaryImage';

/**
 * Lazily load a session-only `AvailableImage` into the local patient
 * repository. In compatibility mode the connector only indexes the
 * session's `ContextImage`; any other series surfaced through
 * `session.payload.images` is visible to the UI but has not been
 * pulled from PACS yet, so the zip endpoint would 500. The hook
 * therefore asks the connector to fetch the series first
 * (`/api/images/request-download`), polls the zip endpoint via
 * `withRetryOn202` until the worker is done, processes the resulting
 * archive via `useProcessDicomFiles`, bumps `imageListVersionState` so
 * any UI showing the image list refreshes, and returns the resolved
 * `Image3D`. Returns `undefined` if the download / processing fails or
 * if no matching image is produced.
 *
 * The hook intentionally does not show a global busy indicator or
 * snackbar; callers own the UX so the same helper works both for
 * synchronous flows (user clicks a download button and waits) and
 * fire-and-forget flows (parallel download kicked off alongside a
 * task submission). Callers that want feedback can wrap the returned
 * promise themselves.
 */
export default function useLoadAvailableImage() {
    const { processImportedFiles } = useProcessDicomFiles();
    const setImageListVersion = useSetRecoilState(imageListVersionState);
    const sessionData = useRecoilValue(sessionDataAtom);
    const sessionPatientId = sessionData?.payload?.patient?.id;

    const loadAvailableImage = useCallback(
        async (image: AvailableImage): Promise<Image3D | undefined> => {
            if (image.imageObject) {
                return image.imageObject;
            }
            if (sessionPatientId) {
                await fetchRequestImageDownload({
                    body: {
                        patientId: sessionPatientId,
                        studyInstanceUID: image.studyInstanceUID,
                        seriesInstanceUID: image.seriesInstanceUID,
                        modality: image.modality,
                    },
                });
            }
            const dicomBlob = await withRetryOn202(
                async () =>
                    await fetchDownloadDicomSeries({
                        pathParams: {
                            studyId: image.studyInstanceUID,
                            seriesId: image.seriesInstanceUID,
                        },
                        queryParams: { series: true },
                    })
            );
            const files = [
                new File([dicomBlob], `${image.seriesInstanceUID}.zip`),
            ];
            const result = await processImportedFiles(files);
            setImageListVersion((v) => v + 1);
            return result?.images?.find(
                (img) => img.seriesUid === image.seriesInstanceUID
            );
        },
        [processImportedFiles, sessionPatientId, setImageListVersion]
    );

    return loadAvailableImage;
}
